import { useState, useCallback, useRef, useEffect } from 'react';
import { geminiService, GeminiApiError } from '../services/gemini.service';
import { authService } from '../services/auth.service';
import { ChatMessage, ChatTurn, GeminiModel, StudentContext } from '../types/ai.types';
import { registerGlobalAudio, registerGlobalAbortController } from '../utils/speechQueueManager';

const API_BASE = (import.meta.env.PROD && (import.meta.env.VITE_API_BASE_URL === 'http://localhost:8080' || !import.meta.env.VITE_API_BASE_URL)) ? '' : (import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080');

const WELCOME_MESSAGE: ChatMessage = {
  id: 'welcome',
  sender: 'ai',
  text: 'Merhaba! 👋 Ben senin AI mentorunum. Bugün ne öğrenmek istersin?',
  createdAt: Date.now(),
};

function createId() {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

export function useAiMentorChat(studentContext?: StudentContext) {
  const [messages, setMessages] = useState<ChatMessage[]>([WELCOME_MESSAGE]);
  const [isLoading, setIsLoading] = useState(false);
  const [model, setModel] = useState<GeminiModel>('gemini-3.6-flash');
  const [isQuotaExceeded, setIsQuotaExceeded] = useState(false);
  const [isGlobalAudioPlaying, setIsGlobalAudioPlaying] = useState(false);
  const [isStreaming, setIsStreaming] = useState(false);

  const historyRef = useRef<ChatTurn[]>([]);
  const studentContextRef = useRef<StudentContext | undefined>(studentContext);
  studentContextRef.current = studentContext;
  const modelRef = useRef<GeminiModel>(model);
  modelRef.current = model;
  
  const isLoadingRef = useRef(false);
  isLoadingRef.current = isLoading;
  const isSendingRef = useRef(false);

  const audioQueueRef = useRef<{ text: string; audioBase64: string }[]>([]);
  const isPlayingQueueRef = useRef(false);
  const currentAudioRef = useRef<HTMLAudioElement | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  const stopAudio = useCallback(() => {
    // 1. Devam eden stream isteğini iptal et
    if (abortControllerRef.current) {
      try { abortControllerRef.current.abort(); } catch {}
      abortControllerRef.current = null;
    }

    // 2. Kuyruktaki sesleri boşalt
    audioQueueRef.current = [];
    isPlayingQueueRef.current = false;

    // 3. Çalmakta olan sesi anında durdur ve sıfırla
    if (currentAudioRef.current) {
      const audio = currentAudioRef.current;
      audio.onplay = null;
      audio.onpause = null;
      audio.onended = null;
      audio.onerror = null;
      try {
        audio.pause();
        audio.currentTime = 0;
        audio.src = '';
      } catch {}
      currentAudioRef.current = null;
    }

    setIsGlobalAudioPlaying(false);
    setIsStreaming(false);
    setIsLoading(false);
    isSendingRef.current = false;
  }, []);

  // Component unmount olduğunda veya global durdurma tetiklendiğinde sesi anında kes
  useEffect(() => {
    const handleGlobalStop = () => {
      stopAudio();
    };

    window.addEventListener('app:audio-stopped', handleGlobalStop);

    return () => {
      window.removeEventListener('app:audio-stopped', handleGlobalStop);
      stopAudio();
    };
  }, [stopAudio]);

  const playNextAudio = useCallback(() => {
    if (isPlayingQueueRef.current || audioQueueRef.current.length === 0) return;
    
    isPlayingQueueRef.current = true;
    const nextAudio = audioQueueRef.current.shift();
    if (!nextAudio || !nextAudio.audioBase64) {
      isPlayingQueueRef.current = false;
      playNextAudio();
      return;
    }

    const audioUrl = `data:audio/mp3;base64,${nextAudio.audioBase64}`;
    const audio = new Audio(audioUrl);
    registerGlobalAudio(audio);
    currentAudioRef.current = audio;

    audio.onplay = () => setIsGlobalAudioPlaying(true);
    audio.onpause = () => setIsGlobalAudioPlaying(false);

    audio.onended = () => {
      setIsGlobalAudioPlaying(false);
      isPlayingQueueRef.current = false;
      playNextAudio();
    };

    audio.onerror = (e) => {
      console.error("Audio error", e);
      setIsGlobalAudioPlaying(false);
      isPlayingQueueRef.current = false;
      playNextAudio();
    };

    audio.play().catch(err => {
      if (err.name === 'AbortError') return;
      console.warn("Auto play blocked:", err);
      setIsGlobalAudioPlaying(false);
      isPlayingQueueRef.current = false;
      playNextAudio();
    });
  }, []);

  const toggleGlobalAudio = useCallback(() => {
    if (currentAudioRef.current) {
      if (currentAudioRef.current.paused) {
        currentAudioRef.current.play().catch(e => console.warn(e));
      } else {
        currentAudioRef.current.pause();
      }
    }
  }, []);

  const sendMessage = useCallback(
    async (text: string, options?: { isLessonRequest?: boolean }) => {
      const trimmed = text.trim();
      if (!trimmed || isSendingRef.current || isLoadingRef.current) return;

      isSendingRef.current = true;
      const userMsg: ChatMessage = {
        id: createId(),
        sender: 'user',
        text: trimmed,
        createdAt: Date.now(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setIsLoading(true);

      try {
        const token = await authService.getAccessToken();
        if (!token) throw new Error("Oturum bulunamadı");
        
        const controller = new AbortController();
        abortControllerRef.current = controller;
        const unregisterController = registerGlobalAbortController(controller);

        const response = await fetch(`${API_BASE}/api/ai/stream-chat`, {
          method: 'POST',
          signal: controller.signal,
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`
          },
          body: JSON.stringify({
            message: trimmed,
            history: historyRef.current,
            model: modelRef.current,
            studentContext: studentContextRef.current,
            isLessonRequest: options?.isLessonRequest
          })
        });

        if (!response.ok) {
          const data = await response.json().catch(() => ({}));
          throw new GeminiApiError(data?.error?.message || 'İstek başarısız', response.status, data?.error?.code);
        }
        
        setIsQuotaExceeded(false);

        const aiMessageId = createId();
        setMessages((prev) => [
          ...prev,
          {
            id: aiMessageId,
            sender: 'ai',
            text: '',
            createdAt: Date.now(),
          },
        ]);
        
        setIsLoading(false); // Yazıyor animasyonunu durdur, stream başlıyor
        setIsStreaming(true);

        const reader = response.body?.getReader();
        if (!reader) throw new Error('Stream başlatılamadı');
        const decoder = new TextDecoder();
        let streamBuffer = '';
        let fullText = '';
        
        let streamDone = false;
        while (!streamDone) {
          if (controller.signal.aborted) break;
          const { done, value } = await reader.read();
          if (done) break;

          streamBuffer += decoder.decode(value, { stream: true });
          const lines = streamBuffer.split('\n');
          streamBuffer = lines.pop() || '';

          for (const line of lines) {
            if (controller.signal.aborted) {
              streamDone = true;
              break;
            }
            const lineTrimmed = line.trim();
            if (!lineTrimmed.startsWith('data:')) continue;

            const jsonStr = lineTrimmed.substring(5).trim();
            if (jsonStr === '[DONE]') continue;
            
            try {
              const data = JSON.parse(jsonStr);
              if (data.isFinished) {
                streamDone = true;
                break;
              }

              if (data.text) {
                fullText += (fullText ? ' ' : '') + data.text;
                
                setMessages(prev => prev.map(m => 
                  m.id === aiMessageId ? { ...m, text: fullText } : m
                ));

                if (data.audioBase64 && !controller.signal.aborted) {
                  audioQueueRef.current.push({ text: data.text, audioBase64: data.audioBase64 });
                  playNextAudio();
                }
              }
            } catch(e) {
               console.warn(e);
            }
          }
        }

        unregisterController();

        historyRef.current = [
          ...historyRef.current,
          { role: 'user', parts: [{ text: trimmed }] },
          { role: 'model', parts: [{ text: fullText }] },
        ];
      } catch (err: any) {
        if (err?.name === 'AbortError') {
          // İsteğin kullanıcı tarafından durdurulması/sayfadan çıkılması normal bir durumdur
          return;
        }
        if (err instanceof GeminiApiError && err.code === 'QUOTA_EXCEEDED') {
          setIsQuotaExceeded(true);
          setMessages((prev) => [
            ...prev,
            {
              id: createId(),
              sender: 'ai',
              text: `⚠️ ${err.message}`,
              isError: true,
              createdAt: Date.now(),
            },
          ]);
        } else {
          const message =
            err instanceof GeminiApiError
              ? err.message
              : 'Beklenmeyen bir hata oluştu. Lütfen tekrar dene.';
          setMessages((prev) => [
            ...prev,
            { id: createId(), sender: 'ai', text: message, isError: true, createdAt: Date.now() },
          ]);
        }
      } finally {
        isSendingRef.current = false;
        setIsLoading(false);
        setIsStreaming(false);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [playNextAudio]
  );

  const clearChat = useCallback(() => {
    historyRef.current = [];
    setIsQuotaExceeded(false);
    stopAudio();
    setMessages([
      {
        id: createId(),
        sender: 'ai',
        text: 'Sohbet temizlendi! Yeni bir konuşmaya başlayabilirsin. 👋',
        createdAt: Date.now(),
      },
    ]);
  }, [stopAudio]);

  return { messages, isLoading, isStreaming, sendMessage, clearChat, stopAudio, model, setModel, isQuotaExceeded, isGlobalAudioPlaying, toggleGlobalAudio };
}
